import { FACTORY_ADDRESS } from '@emberswap/emberswap-sdk2/dist/constants'
import type { PoolCode } from '@emberswap/emberswap-sdk2/dist/entities'
import {
  Amount,
  MultiRoute,
  Native,
  Token,
  Trade,
} from '@emberswap/emberswap-sdk2/dist/entities'
import { ChainId, TradeType, Version } from '@emberswap/emberswap-sdk2/dist/enums'
import { Type as Currency } from '@emberswap/emberswap-sdk2/dist/types'
import { BigNumber } from '@ethersproject/bignumber'
import { useMemo } from 'react'
import { useFeeData } from 'wagmi'

import { EmberSwapV2Provider } from '../classes/liquidity-providers/EmberswapV2'
import { MistSwapV2Provider } from '../classes/liquidity-providers/MistswapV2'
import { Router } from '../classes/Router'
import { useCustomTokens, useSettings } from '../state/user/hooks'
import { useTokens } from './Token/useTokens'
import { useCurrencyCombinations } from './useCurrencyCombinations'
import {
  usePairs,
} from './usePairs'



export const AMM_ENABLED_NETWORKS = [
  ChainId.SMARTBCH,
  ChainId.SMARTBCH_TESTNET,
  ChainId.BSC,
  ChainId.BSC_TESTNET
]

export type UseTradeOutput = {
  trade:
    | Trade<Currency, Currency, TradeType.EXACT_INPUT | TradeType.EXACT_OUTPUT, Version.V1 | Version.V2>
    | undefined
  route: MultiRoute | undefined
  pairs: Map<string, PoolCode> | undefined
}

/**
 * @param {number | undefined} chainId
 * @param {TradeType.EXACT_INPUT | TradeType.EXACT_OUTPUT} tradeType whether we request an exact output amount or we provide an exact input amount
 * @param {Amount<Currency>} amountSpecified the exact amount to swap in/out
 * @param {Currency} mainCurrency the desired input/payment currency
 * @param {Currency} otherCurrency the desired output/payment currency
 * @param {Record<number, Record<string, TokenAsObject>>} userPath the desired path through Aggregator.
 * @param {Token[]} _tokenMap
 * @param {'auto'|'custom'} pathType whether pathing should be automated or using custom mid tokens.
 * @return {UseTradeOutput} Returns trade for a desired swap.
 */
export function useTrade(
  chainId: number,
  tradeType: TradeType.EXACT_INPUT | TradeType.EXACT_OUTPUT,
  amountSpecified: Amount<Currency> | undefined,
  mainCurrency: Currency | undefined,
  otherCurrency: Currency | undefined,
): UseTradeOutput {
  const { data: feeData } = useFeeData({
    chainId,
  })

  const [customTokensMap] = useCustomTokens(chainId)
  const tokenMap = useTokens(chainId)
  const _tokenMap: Token[] = useMemo(
    () => Object.values({ ...tokenMap, ...customTokensMap }),
    [tokenMap, customTokensMap]
  )
  const [{ userPath, userPathType, userExpertMode }] = useSettings(chainId)

  const [currencyIn, currencyOut] = useMemo(
    () =>
      tradeType === TradeType.EXACT_INPUT
        ? [mainCurrency, otherCurrency]
        : [otherCurrency, mainCurrency],
    [tradeType, mainCurrency, otherCurrency]
  )

  const _midAddresses = useMemo(() => {
    const path =
      userExpertMode && userPath?.[chainId] 
      ?
      Object.values(userPath[chainId])
        .map((token) => token.address)
        .filter((address) => address != otherCurrency?.wrapped.address)
        .sort((a, b) => {
          if (a === Native.onChain(chainId).wrapped.address) {
            return 1 // a should be placed after b
          }
          if (b === Native.onChain(chainId).wrapped.address) {
            return -1 // b should be placed after a
          }
          return a.localeCompare(b) // sort in alphabetical order
        }) : ['']
    return _tokenMap?.filter((t) =>
      path?.some((a) => a.toLowerCase() == t.wrapped.address.toLowerCase())
    )
  }, [_tokenMap, chainId, otherCurrency?.wrapped.address, userExpertMode, userPath])

  // Generate currency combinations of input and output token based on configured bases
  const currencyCombinations = useCurrencyCombinations(
    chainId,
    currencyIn,
    currencyOut,
    userExpertMode ? userPathType : 'auto',
    _midAddresses
  )
  const providers = [new EmberSwapV2Provider(chainId), new MistSwapV2Provider(chainId)]
  // Legacy SushiSwap pairs
  const { data: pairs } = usePairs(chainId, currencyCombinations, providers, {
    enabled: Boolean(chainId && AMM_ENABLED_NETWORKS.includes(chainId)),
  })

  return useMemo(() => {
    if (
      feeData &&
      feeData.gasPrice &&
      currencyIn &&
      currencyOut &&
      currencyIn.wrapped.chainId === currencyOut.wrapped.chainId &&
      currencyIn.wrapped.chainId === chainId &&
      currencyOut.wrapped.chainId === chainId &&
      currencyIn.wrapped.address !== currencyOut.wrapped.address &&
      chainId &&
      amountSpecified &&
      amountSpecified.greaterThan(0) &&
      otherCurrency &&
      pairs &&
      pairs.size > 0
    ) {
      if (chainId in FACTORY_ADDRESS) {
        const bestRoute = Router.findBestRoute(
          pairs,
          chainId,
          currencyIn,
          BigNumber.from(amountSpecified.quotient.toString()),
          currencyOut,
          Number(feeData.gasPrice) ?? 30e9
        )

        return {
          trade: Trade.exactIn(bestRoute, amountSpecified, currencyOut, Version.V1),
          route: bestRoute,
          pairs: pairs,
        }
      }
    }
    return {
      trade: undefined,
      route: undefined,
      pairs: undefined
    }
  }, [feeData, currencyIn, currencyOut, chainId, amountSpecified, otherCurrency, pairs])
}
